<template>
  <div class="map">
    <v-row class="map__buttons flex-column" dense>
      <v-col>
        <v-speed-dial v-model="fab" direction="right" class="map__btn">
          <template v-slot:activator>
            <v-btn
              v-model="fab"
              color="primary"
              dark
              fab
              x-small
              :title="
            fab
              ? $i18n.t('dashboard.source.measurement.cancel')
              : !measureType
                ? $i18n.t('dashboard.source.measurement.sketchesAndMeasurements')
                : $i18n.t('dashboard.source.measurement.delete')
          "
              @click="clearMeasurement"
            >
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else-if="!fab && measureType">mdi-delete</v-icon>
              <v-icon v-else>mdi-ruler</v-icon>
            </v-btn>
          </template>
          <base-map-button
            title="dashboard.source.measurement.linearSketch"
            @click="addMeasurement('LineString')"
            icon="mdi-chart-line-variant"
          />
          <base-map-button
            title="dashboard.source.measurement.areaSketch"
            @click="addMeasurement('Polygon')"
            icon="mdi-ruler-square"
          />
        </v-speed-dial>
      </v-col>
      <v-col>
        <base-map-button
          title="dashboard.map.zoomToStart"
          @click="zoomToStartView"
          icon="mdi-fullscreen"
        />
      </v-col>
      <v-col v-if="$route.params.id">
        <base-map-button title="offer.zoomToOffer" @click="zoomToOffer" icon="mdi-magnify" />
      </v-col>
      <v-col>
        <base-map-button title="dashboard.map.legend" icon="mdi-map-legend" @click="openMapLegend" />
      </v-col>
      <v-col>
        <!-- <base-map-button title="dashboard.map.print" icon="mdi-printer" @click="print" /> -->
      </v-col>
      <v-col>
        <base-map-button title="dashboard.map.print" icon="mdi-printer" @click="isMapPrintingDialogVisible = true" />
      </v-col>
      <template v-if="hasFilter">
        <v-col v-if="!isDrawingActive && !isFilter">
          <base-map-button title="dashboard.map.filter" icon="mdi-filter" @click="startFilter()" />
        </v-col>
        <v-col v-if="isDrawingActive && isFilter">
          <base-map-button
            title="dashboard.map.accept"
            icon="mdi-check"
            @click="acceptDrawedPolygon()"
          />
        </v-col>
        <v-col v-if="isDrawingActive && isFilter">
          <base-map-button
            title="dashboard.map.cancel"
            icon="mdi-close"
            @click="cancelDrawingPolygon()"
          />
        </v-col>
      </template>
      <slot name="map-buttons"></slot>
    </v-row>
    <map-address-search @search="searchAddress" @clear="clearSearchAddress" />
    <layers-loader :items="layersLoader" />
    <map-zoom-level :zoom-level="mapZoom" />
    <v-row class="map__buttons-right flex-column" dense>
      <v-col v-if="editingLayer">
        <base-map-button
          :title="isUserLayerEditing?'dashboard.map.stopLayerEdit':'dashboard.map.startLayerEdit'"
          :icon="isUserLayerEditing? 'mdi-cancel' : 'mdi-pencil'"
          @click="layerEdit"
        />
      </v-col>
      <v-col v-if="isUserLayerEditing">
        <base-map-button
          :title="$i18n.t('dashboard.map.add' + editLayer.geometry_type)"
          :icon="editingTypeIcon"
          @click="addFeature"
        />
      </v-col>
      <v-col v-if="isUserLayerEditing">
        <base-map-button
          title="dashboard.map.selectFeature"
          icon="mdi-cursor-pointer"
          @click="selectFeature"
        />
      </v-col>
    </v-row>
    <MapLegendDialog
      v-if="isLegendLoaded"
      ref="map_legend"
      :dialog="isLegendActive"
      :isDashboard="true"
      @toggleWMS="toggleWMS"
      @toggleUserLayer="toggleUserLayer"
      @hideLegend="isLegendActive = false"
    >
      <template v-slot:buttons>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="primary"
            text
            @click="isLegendActive = false"
          >{{ $i18n.t('dialog.confirmation.close') }}</v-btn>
        </v-card-actions>
      </template>
    </MapLegendDialog>
    <MapPrintingDialog :is-visible.sync="isMapPrintingDialogVisible" :parent-map="map" printed-layer-name="offers" />
    <AddNewFeatureDialog
      :dialog="isAddNewFeatureActive"
      :fields="editLayer.fields || []"
      @success="saveUserFeature"
      @cancel="cancelUserFeature"
    />
    <FeatureDialog
      :dialog="isFeatureActive"
      :fields="editLayer.fields || []"
      :selectedFeatureProperties="selectedUserFeature?selectedUserFeature.get('properties'):{}"
      @delete="deleteFeature"
      @cancel="hideFeature"
    />
  </div>
</template>

<script>
import Map from 'ol/Map';
import View from 'ol/View';
import _ from 'lodash';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { GeoJSON, WKT } from 'ol/format';
import Collection from 'ol/Collection';
import Select from 'ol/interaction/Select';
import Draw from 'ol/interaction/Draw';
import { Style, Stroke, Fill, Circle, Text, Icon } from 'ol/style';
import proj4 from 'proj4';
// import {fromLonLat} from 'ol/proj';
import { register } from 'ol/proj/proj4';
import pointsWithinPolygon from '@turf/points-within-polygon';
import explode from '@turf/explode';
import { LineString, Polygon } from 'ol/geom.js';
import { getArea, getLength } from 'ol/sphere.js';
import { unByKey } from 'ol/Observable.js';
import Overlay from 'ol/Overlay.js';
import { defaults as defaultControls, FullScreen, ScaleLine } from 'ol/control.js';
import TileLayer from 'ol/layer/Tile';
import TileGrid from 'ol/tilegrid/TileGrid';
import { getWidth, getCenter } from 'ol/extent';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import MultiPoint from 'ol/geom/MultiPoint';
import TileWMS from 'ol/source/TileWMS';
// import MapPrinting from '@/components/MapPrinting';
import { basemaps } from '@/models/Basemaps';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import { get as getProjection } from 'ol/proj';
import axios from 'axios';
import { getFeatureCentroid } from '@/utils/mapUtils';

import { getWMSLayers } from '@/utils/mapUtils';
import searchAddressMixin from '@/mixins/searchAddressMixin';
import layersLoaderMixin from '@/mixins/layersLoaderMixin';
import userLayersMixin from '@/mixins/userLayersMixin';

import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

export default {
  mixins: [searchAddressMixin, layersLoaderMixin, userLayersMixin],
  props: {
    offers: {
      type: Array,
      required: true
    },
    singleOffer: {
      type: Boolean,
      required: false,
      default: false
    },
    isFilter: {
      required: false,
      default: false
    },
    isDrawingActive: {
      required: false
    },
    hasFilter: {
      type: Boolean,
      default: true
    }
  },
  components: {
    // MapPrinting: () => import('@/components/MapPrinting'),
    MapLegendDialog: () => import('./MapLegendDialog'),
    AddNewFeatureDialog: () => import('@/components/dashboard/AddNewFeatureDialog'),
    FeatureDialog: () => import('@/components/dashboard/FeatureDialog'),
    MapAddressSearch: () => import('@/components/MapAddressSearch'),
    LayersLoader: () => import('@/components/LayersLoader'),
    MapPrintingDialog: () => import('@/components/printing/MapPrintingDialog'),
    MapZoomLevel: () => import('@/components/MapZoomLevel')
  },
  data: () => ({
    fab: false,
    featuresInExtent: [],
    measureTooltip: undefined,
    measureTooltipElement: undefined,
    measureType: undefined,
    offersAllSource: undefined,
    sketch: undefined,
    // isPrintingVisible: false,
    isMapPrintingDialogVisible: false,

    // modals
    isLegendActive: false,
    isLegendLoaded: false,

    // isDrawingActive: false,
    // isFilter: undefined,

    parcelsToAdd: [],
    map: undefined
  }),
  computed: {
    wmsLayers() {
      return this.$store.getters.getWMSLayers;
    },
    currentBasemap() {
      return this.$store.getters.getCurrentBasemap;
    },
    offersStyle() {
      return this.$store.getters.getOffersStyle;
    },
    parcelsStyle() {
      return this.$store.getters.getParcelsStyle;
    },
    mapZoom() {
      if (this.map) {
        return this.map.getView().getZoom();
      }
      return 0;
    }
  },
  watch: {
    offers(nV) {
      this.loadOffers(nV);
    },
    currentBasemap(nV) {
      this.changeBaselayer(nV);
    },
    wmsLayers(nV) {
      this.addWmsLayers(nV);
    }
  },
  methods: {
    // print() {
    //   this.isPrintingVisible = true;
    // },
    addMeasurement(type) {
      this.getInteractionByName('selection').setActive(false); //block offers selection
      const drawLayer = this.getLayerByName('measurement');
      const drawInteraction = this.getInteractionByName('drawMeasurement');

      if (drawInteraction && this.measureType != type) {
        this.map.removeInteraction(drawInteraction);
        this.addMeasurementInteraction(drawLayer, type);
      } else {
        this.addMeasurementInteraction(drawLayer, type);
      }

      this.measureType = type;
      this.getInteractionByName('drawMeasurement').setActive(true);
      this.createMeasureTooltip();
    },
    addMeasurementInteraction(layerName, type) {
      const drawInteraction = new Draw({
        source: layerName.getSource(),
        type: type,

        style: feature => {
          const geometry = feature.getGeometry();
          const styles = [];

          if (type === 'LineString' && geometry instanceof LineString) {
            geometry.forEachSegment((start, end) => {
              const line = new LineString([start, end]);
              styles.push(
                new Style({
                  geometry: line,
                  fill: new Fill({
                    color: `${vuetifyColors.accent}30`,
                  }),
                  stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.5)',
                    lineDash: [10, 10],
                    width: 2
                  }),
                  text: new Text({
                    text: this.formatLength(line),
                    font: '15px sans-serif',
                    fill: new Fill({ color: 'black' }),
                    placement: 'line',
                    stroke: new Stroke({
                      color: `${vuetifyColors.accent}30`,
                      width: 3
                    }),
                    offsetX: -20,
                    offsetY: 20
                  }),
                  image: new Circle({
                    radius: 5,
                    stroke: new Stroke({
                      color: vuetifyColors.accent
                    }),
                    fill: new Fill({
                      color: `${vuetifyColors.accent}50`
                    })
                  })
                })
              );
            });
          } else {
            styles.push(
              new Style({
                fill: new Fill({
                  color: `${vuetifyColors.accent}30`
                }),
                stroke: new Stroke({
                  color: 'rgba(0, 0, 0, 0.5)',
                  lineDash: [10, 10],
                  width: 2
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: `${vuetifyColors.accent}50`
                  })
                })
              })
            );
          }
          return styles;
        }
      });
      drawInteraction.set('name', 'drawMeasurement');
      this.map.addInteraction(drawInteraction);

      let listener;
      drawInteraction.on('drawstart', evt => {
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        // set sketch
        this.sketch = evt.feature;

        listener = this.sketch.getGeometry().on('change', evt => {
          if (evt.target instanceof Polygon) {
            const result = `${this.formatArea(evt.target)} <br> ${this.formatLength(evt.target)}`;
            this.measureTooltipElement.innerHTML = result;
            this.measureTooltip.setPosition(evt.target.getInteriorPoint().getCoordinates());
          } else if (evt.target instanceof LineString) {
            this.measureTooltipElement.innerHTML = this.formatLength(evt.target);
            this.measureTooltip.setPosition(evt.target.getLastCoordinate());
          }
        });
      });

      drawInteraction.on('drawend', () => {
        this.measureTooltipElement.className = 'tooltip tooltip-static';
        this.measureTooltip.setOffset([0, -7]);
        // unset sketch
        this.sketch = null;
        unByKey(listener);
      });
    },
    // Dodanie narysowanego ficzera do warstwy działek
    addParcel() {
      let parcel = this.getLayerByName('polygonDrawLayer')
        .getSource()
        .getFeatures()[0];
      this.getLayerByName('polygonDrawLayer')
        .getSource()
        .clear();
      this.getLayerByName('parcels')
        .getSource()
        .addFeature(parcel);
      // TODO - usunąć snackbar
      this.$root.$emit('showSnackbar', 'Dodano działkę');
    },
    addWmsLayers(layers) {
      for (let layer of layers) {
        let projExtent;
        if (getProjection(layer.wms_srs).getExtent()) {
          projExtent = getProjection(layer.wms_srs).getExtent();
        } else {
          projExtent = getProjection('EPSG:3857').getExtent();
        }
        let startResolution = getWidth(projExtent) / 512;
        let resolutions = new Array(22);
        for (let i = 0, ii = resolutions.length; i < ii; ++i) {
          resolutions[i] = startResolution / Math.pow(2, i);
        }
        
        const source = new TileWMS({
          url: `${process.env.VUE_APP_DIVI_BASE_URL}/wms_proxy/${layer.wms_url}`,
          projection: layer.wms_srs,
          params: {
            LAYERS: layer.wms_layers,
            TILED: true,
            VERSION: '1.1.1'
          },
          tileGrid: new TileGrid({
            extent: projExtent,
            resolutions: resolutions,
            tileSize: [512, 512]
          })
        });
        this.map.addLayer(
          new TileLayer({
            name: layer.name_pl,
            visible: false,
            source
          })
        );
        this.addLayersLoaderSource(layer.id, { pl: layer.name_pl, en: layer.name_en }, source);
      }
      this.isLegendLoaded = true;
    },
    cancelDrawingPolygon() {
      if (!this.isFilter) {
        this.getLayerByName('offers').setVisible(true);
        this.getLayerByName('parcels').setVisible(false);
      }
      this.$emit('update:isFilter', false);
      this.$emit('update:isDrawingActive', false);
      this.getLayerByName('polygonDrawLayer')
        .getSource()
        .clear();
      this.getInteractionByName('polygonInteraction').setActive(false);
      this.getInteractionByName('selection').setActive(true);
      this.$emit('cancelFiltering');
    },
    clearMeasurement(e) {
      if (!this.fab && this.measureType) {
        e.stopPropagation();
        this.fab = false;
        this.measureType = false;
        this.getInteractionByName('drawMeasurement').setActive(false);
        this.getInteractionByName('selection').setActive(true);
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        this.measureTooltipElement.parentNode.removeChild(this.measureTooltipElement); //clear tooltip
      }
    },
    getULDKParcel(e) {
      this.map.un('click', this.getULDKParcel);
      this.$refs.map.style.cursor = 'default';
      this.$store.dispatch('getParcelULDK', e.coordinate).then(r => {
        if (r.status === 200) {
          if (r.data.slice(0, 2) === '-1') {
            this.$root.$emit('showSnackbar', this.$i18n.t('dashboard.map.parcelNotFound'));
          } else {
            let [epsg, parcelData] = r.data.split(';');
            const [wkt /*voivodeship, county, region, parcel*/] = parcelData.split('|');
            const parcelFeature = new WKT({}).readFeature(wkt, {
              dataProjection: `EPSG:${epsg.split('=')[1]}`,
              featureProjection: 'EPSG:3857'
            });
            this.getLayerByName('parcels')
              .getSource()
              .addFeature(parcelFeature);
            this.zoomToFeature(parcelFeature);
          }
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t('dashboard.map.parcelError' + r.data));
        }
      });
    },
    changeBaselayer(layerName) {
      for (let el of this.map.getLayers().getArray()) {
        if (el.get('group') === 'baselayers') {
          if (el.get('name') === layerName) {
            el.setVisible(true);
          } else {
            el.setVisible(false);
          }
        }
      }
    },
    createMeasureTooltip() {
      this.measureTooltipElement = document.createElement('div');
      this.measureTooltipElement.className = 'tooltip tooltip-measure';
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: 'bottom-center'
      });
      this.map.addOverlay(this.measureTooltip);
    },
    formatArea(polygon) {
      var area = getArea(polygon);
      var output;
      if (area > 10000) {
        output = Math.round((area / 10000) * 100) / 100 + ' ' + 'ha';
      } else {
        output = Math.round((area / 10000) * 1000) / 1000 + ' ' + 'ha';
      }
      return output;
    },
    formatLength(line) {
      var length = getLength(line);
      var output;
      if (length > 100) {
        output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
      } else {
        output = Math.round(length * 100) / 100 + ' ' + 'm';
      }
      return output;
    },
    formatProjectOfferText(area, custom_id) {
      //const areaLabel = this.$i18n.locale === 'pl' ? 'Powierzchnia' : 'Area';
      //const numberLabel = this.$i18n.locale === 'pl' ? 'Nr' : 'No';
      let output = '';
      // if (custom_id) {
      //   output = numberLabel + ': ' + custom_id + '\n';
      // }
      if (area > 10000) {
        output += area + ' ha';
      } else if (!area) {
        output += '0' + ' ha';
      } else {
        output += area + ' ha';
      }
      return output;
    },
    getInteractionByName(name) {
      return this.map
        .getInteractions()
        .getArray()
        .find(i => i.get('name') == name);
    },
    getLayerByName(name) {
      return this.map
        .getLayers()
        .getArray()
        .find(l => {
          return l.get('name') == name;
        });
    },
    startFilter() {
      this.$emit('update:isFilter', true);
      this.$emit('update:isDrawingActive', true);
      this.startDrawingPolygon(true);
    },
    startDrawingPolygon(isFilter) {
      if (!isFilter) {
        if (this.parcelsToAdd.length === 0) {
          this.$root.$emit('showSnackbar', this.$i18n.t(`default.downloadingParcels`));
          this.$store.dispatch('getParcels').then(data => {
            this.parcelsToAdd = data.filter(parcel => !!parcel).map(parcel => {
              const feature = new GeoJSON().readFeature(parcel.geometry, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
              });
              feature.set('id', parcel.id);
              return feature;
            });
            this.getLayerByName('parcels')
              .getSource()
              .clear();
            this.getLayerByName('parcels')
              .getSource()
              .addFeatures(this.parcelsToAdd);
            this.$root.$emit('showSnackbar', this.$i18n.t(`dashboard.drawOffer`));
          });
        }
        this.getLayerByName('offers').setVisible(false);
        this.getLayerByName('parcels').setVisible(true);
      }
      // this.isDrawingActive = true;
      this.getInteractionByName('polygonInteraction').setActive(true);
      this.getInteractionByName('selection').setActive(false);
    },
    acceptDrawedPolygon() {
      if (this.isFilter) {
        this.filterWithArea();
      } else {
        this.filterWithArea('newOffer');
      }
    },
    loadOffers(data) {
      const features = [];
      const featuresLabelsPoints = [];
      for (let offer of data) {
        if (offer) {
          const geom = offer.hasOwnProperty('geometry') ? offer.geometry : offer.geometria;
          const feature = new GeoJSON().readFeature(geom, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          const featureLabelPoint = new Feature(new Point(getCenter(feature.getGeometry().getExtent())));
          for (const key in offer) {
            if (key !== 'geometria' && key !== 'geometry') {
              feature.set(key, offer[key]);
              featureLabelPoint.set(key, offer[key]);
            }
          }
          features.push(feature);
          featuresLabelsPoints.push(featureLabelPoint);
        }
      }
      this.getLayerByName('offers')
        .getSource()
        .clear();
      this.getLayerByName('offers')
        .getSource()
        .addFeatures(features);
      if (this.$parent.$parent.$parent._data.project) {
        this.getLayerByName('offersLabels')
          .getSource()
          .clear();
        this.getLayerByName('offersLabels')
          .getSource()
          .addFeatures(featuresLabelsPoints);
      }
      if (!this.singleOffer) {
        this.getFeaturesInView();
      }
    },
    filterWithArea(filterType) {
      const layerName = filterType === 'newOffer' ? 'parcels' : 'offers';
      const features = JSON.parse(
        new GeoJSON().writeFeatures(
          this.getLayerByName(layerName)
            .getSource()
            .getFeatures()
        )
      );
      const filterGeom = this.getLayerByName('polygonDrawLayer')
        .getSource()
        .getFeatures()[0];
      if (!filterGeom) {
        this.$root.$emit('showSnackbar', this.$i18n.t(`dashboard.map.noGeometry`));
        return;
      }
      const filterPolygon = JSON.parse(new GeoJSON().writeFeature(filterGeom));
      const filteredFeatures = pointsWithinPolygon(explode(features), filterPolygon);

      this.$emit(
        'filteredFeaturesWithGeometry',
        filteredFeatures.features.map(feature => {
          return feature.properties.id;
        })
      );
    },
    openMapLegend() {
      this.isLegendActive = true;
    },
    initOrtofoto() {
      return new Promise((resolve, reject) => {
        let parser = new WMTSCapabilities();
        axios
          .get('http://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/ORTO?SERVICE=WMTS&REQUEST=GetCapabilities')
          .then(response => {
            return response.data;
          })
          .then(text => {
            var result = parser.read(text);
            var options = optionsFromCapabilities(result, {
              layer: 'ORTOFOTOMAPA',
              matrixSet: 'EPSG:4326'
            });
            resolve(
              new TileLayer({
                opacity: 1,
                visible: false,
                name: 'Ortofotomapa',
                group: 'baselayers',
                source: new WMTS({
                  url: 'http://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/ORTO',
                  matrixSet: 'EPSG:4326',
                  format: 'image/png',
                  projection: getProjection('EPSG:4326'),
                  tileGrid: options.tileGrid,
                  style: 'default',
                  wrapX: true
                })
              })
            );
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    selectOfferById(id) {
      let interaction = this.getInteractionByName('selection');
      if (interaction) interaction.getFeatures().clear();
      if (id != undefined) {
        let feature = this.getLayerByName('offers')
          .getSource()
          .getFeatures()
          .find(f => f.get('id') == id);
        if (!this.singleOffer) {
          this.zoomToFeature(feature);
        }
        interaction.getFeatures().push(feature);
      }
    },
    setViewData() {
      this.$store.commit('setCenter', this.map.getView().getCenter());
      this.$store.commit('setZoom', this.map.getView().getZoom());
    },
    setView(center, zoom, resetCenter) {
      this.map.getView().setCenter(center);
      this.map.getView().setZoom(zoom);
      if (resetCenter) {
        this.$store.commit('setCenter', null);
        this.$store.commit('setZoom', null);
      }
    },
    toggleWMS(lay) {
      if (this.getLayerByName(lay).getVisible()) {
        this.getLayerByName(lay).setVisible(false);
      } else {
        this.getLayerByName(lay).setVisible(true);
      }
    },
    zoomToFeature(feature) {
      this.map.getView().fit(feature.getGeometry().getExtent(), {
        maxZoom: 16,
        duration: 1000
      });
    },
    zoomToGeometry(geometry, featureProjection = 3857, dataProjection = 3857) {
      const feature = new GeoJSON().readFeature(geometry, {
        featureProjection: `EPSG:${featureProjection}`,
        dataProjection: `EPSG:${dataProjection}`
      });
      this.zoomToFeature(feature);
    },
    zoomToExtent(extent) {
      this.map.getView().fit(extent, { maxZoom: 13, duration: 1000 });
    },
    zoomToOffer() {
      this.zoomToExtent(
        this.getLayerByName('offers')
          .getSource()
          .getExtent()
      );
    },
    zoomToOfferById(oid, layer = 'offers') {
      const feature = this.getLayerByName(layer)
        .getSource()
        .getFeatures()
        .find(f => f.get('id') == oid);
      this.zoomToFeature(feature);
    },
    zoomToParcelByGeometry(geom) {
      this.zoomToGeometry(geom, 3857, 4326);
    },
    zoomToLastView() {
      this.map.getView().setCenter(this.$store.getters.getOfferMapView.center);
      this.map.getView().setZoom(this.$store.getters.getOfferMapView.zoom);
    },
    zoomToStartView() {
      this.map.getView().setCenter([2229729.4005892696, 6903852.286423635]);
      this.map.getView().setZoom(6);
      this.$emit('clearSelection');
    },
    getFeaturesInView() {
      const source = this.getLayerByName('offers').getSource();
      this.featuresInExtent = [];
      const extent = this.map.getView().calculateExtent();
      source.forEachFeatureInExtent(extent, f => {
        this.featuresInExtent.push(f.get('id'));
      });
      this.$emit('filteredFeaturesWithView', this.featuresInExtent);
    },
    getWMSLayers
  },
  mounted() {
    this.getWMSLayers().then(r => {
      this.changeBaselayer(this.currentBasemap);
    });
    proj4.defs(
      'EPSG:2180',
      '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +units=m +no_defs +axis=enu'
    );
    register(proj4);
    getProjection('EPSG:2180').setExtent([144907.1658, 140544.7241, 877004.007, 910679.6817]);
    this.map = window.map = new Map({
      target: this.$el,
      view: new View({
        center: [2229729.4005892696, 6903852.286423635],
        zoom: 6
      }),
      controls: defaultControls().extend([new FullScreen(), new ScaleLine()])
    });
    //this.initOrtofoto().then(r => {
    //this.map.addLayer(r);
    for (let basemap of basemaps) {
      this.map.addLayer(basemap);
    }
    this.map.addLayer(
      new VectorLayer({
        name: 'offers',
        source: new VectorSource({
          features: new Collection([]),
          wrapX: false
        }),
        style: feature => {
          let featureStyle;
          if (this.offersStyle.type === 'unique') {
            let value = '';
            if (this.offersStyle.isArrayValue) {
              const arrayValue =  feature.get(this.offersStyle.attribute).map(el => el[this.offersStyle.classificationTranslate]);
              value = Array.isArray(arrayValue)
                ? arrayValue.filter(value => value !== null)
                    .sort((a, b) => {
                      return (a || '').toString().toLowerCase() > (b || '').toString().toLowerCase() ? 1 : -1;
                    })
                    .join(', ')
                : arrayValue;
            } else {
              if (this.offersStyle.classificationTranslate) {
                value = feature.get(this.offersStyle.attribute)[this.offersStyle.classificationTranslate];
              } else {
                value = feature.get(this.offersStyle.attribute);
              }
            }
            const style = this.offersStyle.uniques[value === null ? '' : value];
            featureStyle = new Style({
              image: new Icon({
                anchor: [12, 28],
                anchorXUnits: 'pixels',
                anchorYUnits: 'pixels',
                src: require('@/assets/marker_offers.svg'),
                color: style && style.fillColor ? style.fillColor : vuetifyColors.accent,
                opacity: style && style.opacity ? 1 - (style.opacity / 100) : 1
              }),
              fill: new Fill({
                color: style && style.fillColor ? style.fillColor : vuetifyColors.accent
              }),
              stroke: new Stroke({
                color: style && style.strokeColor ? style.strokeColor : 'rgb(0,0,0)',
                width: style && style.strokeWidth ? style.strokeWidth : 2
              })
            });
          } else {
            featureStyle = new Style({
              image: new Icon({
                anchor: [12, 28],
                anchorXUnits: 'pixels',
                anchorYUnits: 'pixels',
                src: require('@/assets/marker_offers.svg'),
                color: this.offersStyle && this.offersStyle.fillColor ? this.offersStyle.fillColor : vuetifyColors.accent,
                opacity: this.offersStyle && this.offersStyle.opacity ? 1 - (this.offersStyle.opacity / 100) : 1
              }),
              fill: new Fill({
                color: this.offersStyle && this.offersStyle.fillColor ? this.offersStyle.fillColor : vuetifyColors.accent
              }),
              stroke: new Stroke({
                color: this.offersStyle && this.offersStyle.strokeColor ? this.offersStyle.strokeColor : 'rgb(0,0,0)',
                width: this.offersStyle && this.offersStyle.strokeWidth ? this.offersStyle.strokeWidth : 2
              })
            });
          }
          if (this.map.getView().getZoom() < 12) {
            const centroid = getFeatureCentroid(feature);
            const point = new Point(centroid.geometry.coordinates);
            featureStyle.setGeometry(point);
          }
          return featureStyle;
        }
      })
    );

    let selectInteraction = new Select({
      layers: [this.getLayerByName('offers')],
      style: feature => {
        let style;
        if (this.$parent.$parent.$parent._data.project) {
          style = new Style({
              image: new Icon({
                anchor: [12, 28],
                anchorXUnits: 'pixels',
                anchorYUnits: 'pixels',
                src: require('@/assets/marker_offers.svg'),
                color: 'yellow'
              }),
              fill: new Fill({
                color: 'rgba(230, 230, 65, 1)'
              }),
              stroke: new Stroke({
                color: 'rgba(170, 170, 40, 1)',
                width: 4
              })
            })
        } else {
          style = new Style({
              image: new Icon({
                anchor: [12, 28],
                anchorXUnits: 'pixels',
                anchorYUnits: 'pixels',
                src: require('@/assets/marker_offers.svg'),
                color: 'yellow'
              }),
              fill: new Fill({
                color: 'rgba(230, 230, 65, 1)'
              }),
              stroke: new Stroke({
                color: 'rgba(170, 170, 40, 1)',
                width: 4
              })
            })
        }
        if (this.map.getView().getZoom() < 12) {
          const centroid = getFeatureCentroid(feature);
          const point = new Point(centroid.geometry.coordinates);
          style.setGeometry(point);
        }
        return style;
      }
    });
    selectInteraction.set('name', 'selection');
    selectInteraction.on('select', e => {
      let id = undefined;
      if (e.selected.length > 0) {
        id = e.selected[0].get('id');
        this.selectOfferById(id);
      }
      this.$emit('onSelectedId', id);
    });
    selectInteraction.getOverlay().setZIndex(0);
    this.map.addInteraction(selectInteraction);
    // this.loadOffers(this.offers);

    if (this.$parent.$parent.$parent._data.project) {
      this.map.addLayer(
        new VectorLayer({
          name: 'offersLabels',
          source: new VectorSource({
            features: [],
            wrapX: false
          }),
          zIndex: 10,
          style: feature => {
            return new Style({
              text: new Text({
                text:
                  this.map.getView().getZoom() > 13
                    ? this.formatProjectOfferText(feature.values_.pow_dzialki, feature.values_.custom_id)
                    : null,
                font: '15px sans-serif',
                fill: new Fill({ color: 'black' }),
                stroke: new Stroke({
                  color: 'rgba(255, 255, 255, 1)',
                  width: 5
                }),
                overflow: this.map.getView().getZoom() > 13 ? true : false,
                offsetX: 0,
                offsetY: -12
              })
            });
          }
        })
      );
    }

    // Warstwa filtra #geometrycznego
    let drawLayer = new VectorLayer({
      name: 'polygonDrawLayer',
      source: new VectorSource(),
      style: new Style({
        fill: new Fill({
          color: `${vuetifyColors.accent}30`
        }),
        stroke: new Stroke({
          color: vuetifyColors.accent,
          width: 2
        })
      })
    });
    this.map.addLayer(drawLayer);
    const draw = new Draw({
      source: drawLayer.getSource(),
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: `${vuetifyColors.accent}30`
        }),
        stroke: new Stroke({
          color: vuetifyColors.accent,
          width: 2
        }),
        image: new Circle({
          radius: 5,
          stroke: new Stroke({
            color: vuetifyColors.accent
          }),
          fill: new Fill({
            color: `${vuetifyColors.accent}50`
          })
        })
      })
    });
    draw.set('name', 'polygonInteraction');
    draw.setActive(false);
    this.map.addInteraction(draw);
    draw.on('drawend', () => {
      draw.setActive(false);
    });

    // Warstwa do miarki
    let measurementLayer = new VectorLayer({
      name: 'measurement',
      source: new VectorSource({}),
      style: feature => {
        const geometry = feature.getGeometry();
        const styles = [];

        if (geometry instanceof LineString) {
          geometry.forEachSegment((start, end) => {
            const line = new LineString([start, end]);
            styles.push(
              new Style({
                geometry: line,
                stroke: new Stroke({
                  color: vuetifyColors.accent,
                  width: 2
                }),
                fill: new Fill({
                  color: `${vuetifyColors.accent}30`
                }),
                text: new Text({
                  text: this.formatLength(line),
                  font: '15px sans-serif',
                  fill: new Fill({ color: 'black' }),
                  placement: 'line',
                  stroke: new Stroke({
                    color: `${vuetifyColors.accent}30`,
                    width: 3
                  }),
                  offsetX: -20,
                  offsetY: 20
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: `${vuetifyColors.accent}50`
                  })
                })
              })
            );
          });
        } else {
          styles.push(
            new Style({
              stroke: new Stroke({
                color: vuetifyColors.accent,
                width: 2
              }),
              fill: new Fill({
                color: `${vuetifyColors.accent}30`
              })
            })
          );
        }
        return styles;
      }
    });
    this.map.addLayer(measurementLayer);
    // Warstwa działek
    const parcelsLayer = new VectorLayer({
      name: 'parcels',
      source: new VectorSource({}),
      style: feature => {
        const styles = [];
        styles.push(
          new Style({
            stroke: new Stroke({
              color: this.parcelsStyle.strokeColor,
              width: this.parcelsStyle.strokeWidth
            }),
            fill: new Fill({
              color: this.parcelsStyle.fillColor
            })
          })
        );
        return styles;
      }
    });
    this.map.addLayer(parcelsLayer);
    const debounceFilter = _.debounce(this.getFeaturesInView, 800);
    this.map.on('moveend', () => {
      if (!this.singleOffer) {
        debounceFilter();
        if (!this.$route.params.id) {
          this.$store.commit('setOfferMapView', {
            zoom: this.map.getView().getZoom(),
            center: this.map.getView().getCenter()
          });
        }
      }
    });
    if (this.$store.getters.getOfferMapView) {
      this.zoomToLastView();
    }
  }
};
</script>

<style lang="scss">
.ol-zoom {
  top: 2.5em;
}
.map {
  height: 100%;
  width: 100%;
  .ol-control button {
    background-color: var(--v-primary-base);
  }
  .map__buttons {
    position: absolute;
    z-index: 5;
    left: 12px;
    top: 105px;
    max-height: calc(100% - 80px);
    .v-speed-dial__list {
      z-index: 51;
      padding: 0 2px;
    }
  }
  .map__buttons-right {
    position: absolute;
    z-index: 5;
    right: 12px;
    top: 80px;
    max-height: calc(100% - 80px);
    .v-speed-dial__list {
      z-index: 51;
      padding: 0 2px;
    }
  }
}
.tooltip {
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  padding: 4px 8px;
  opacity: 0.7;
  white-space: nowrap;
}
.tooltip-measure {
  opacity: 1;
  font-weight: bold;
}
.tooltip-static {
  background-color: var(--v-primary-base);
  color: white;
  border: 1px solid white;
}
.tooltip-measure:before,
.tooltip-static:before {
  border-top: 6px solid rgba(0, 0, 0, 0.5);
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  content: '';
  position: absolute;
  bottom: -6px;
  margin-left: -7px;
  left: 50%;
}
.tooltip-static:before {
  border-top-color: var(--v-primary-base);
}
</style>
